import { useState, useEffect } from "react";
import { api } from "@/App";
import StaffLayout from "@/components/layout/StaffLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Car, Search, Plus, Loader2, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const StaffRides = () => {
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [creating, setCreating] = useState(false);
  const [success, setSuccess] = useState(null);
  
  const [rideData, setRideData] = useState({
    vehicle_type: "standard",
    distance_miles: "",
    fare_cents: ""
  });

  const searchCustomers = async () => {
    if (!searchTerm.trim()) return;
    try {
      const response = await api.get(`/staff/customers?search=${searchTerm}&limit=10`);
      setCustomers(response.data.customers);
    } catch (error) {
      toast.error("Failed to search customers");
    }
  };

  const handleCreateRide = async (e) => {
    e.preventDefault();
    if (!selectedCustomer) {
      toast.error("Please select a customer");
      return;
    }
    if (!rideData.distance_miles || !rideData.fare_cents) {
      toast.error("Please fill in all fields");
      return;
    }

    setCreating(true);
    try {
      const response = await api.post("/staff/rides", {
        user_id: selectedCustomer.id,
        vehicle_type: rideData.vehicle_type,
        distance_miles: parseFloat(rideData.distance_miles),
        fare_cents: Math.round(parseFloat(rideData.fare_cents) * 100)
      });
      setSuccess(response.data);
      toast.success("Ride created successfully!");
      // Reset form
      setRideData({ vehicle_type: "standard", distance_miles: "", fare_cents: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create ride");
    } finally {
      setCreating(false);
    }
  };

  return (
    <StaffLayout title="Create Ride">
      <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
        {/* Success Card */}
        {success && (
          <Card className="bg-[#4ADE80]/10 border-[#4ADE80]">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#4ADE80]/20 flex items-center justify-center">
                  <CheckCircle className="text-[#4ADE80]" size={24} />
                </div>
                <div className="flex-1">
                  <p className="text-[#4ADE80] font-semibold">Ride Created Successfully!</p>
                  <p className="text-[#A0A0A0] text-sm">
                    {success.points_earned} miles awarded to customer
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setSuccess(null)}
                  className="border-[#4ADE80] text-[#4ADE80] hover:bg-[#4ADE80] hover:text-[#0A0A0B]"
                >
                  Create Another
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Customer Search */}
        <Card className="bg-[#131316] border-[#2A2A2F]">
          <CardHeader>
            <CardTitle className="text-white">1. Select Customer</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3 mb-4">
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, email, or phone..."
                className="flex-1 bg-[#1A1A1E] border-[#2A2A2F] text-white focus:border-[#D4AF37]"
                onKeyDown={(e) => e.key === 'Enter' && searchCustomers()}
                data-testid="customer-search-ride"
              />
              <Button onClick={searchCustomers} className="bg-[#D4AF37] text-[#0A0A0B]">
                <Search size={18} />
              </Button>
            </div>

            {customers.length > 0 && (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {customers.map((customer) => (
                  <div
                    key={customer.id}
                    onClick={() => setSelectedCustomer(customer)}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedCustomer?.id === customer.id
                        ? 'bg-[#D4AF37]/20 border border-[#D4AF37]'
                        : 'bg-[#0A0A0B] hover:bg-[#1A1A1E]'
                    }`}
                    data-testid={`select-customer-${customer.id}`}
                  >
                    <p className="text-white font-medium">{customer.name}</p>
                    <p className="text-[#6B6B6B] text-sm">{customer.email}</p>
                  </div>
                ))}
              </div>
            )}

            {selectedCustomer && (
              <div className="mt-4 p-4 bg-[#D4AF37]/10 rounded-lg border border-[#D4AF37]/30">
                <p className="text-[#D4AF37] text-sm font-medium">Selected Customer:</p>
                <p className="text-white font-semibold">{selectedCustomer.name}</p>
                <p className="text-[#A0A0A0] text-sm">{selectedCustomer.email}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Ride Details */}
        <Card className="bg-[#131316] border-[#2A2A2F]">
          <CardHeader>
            <CardTitle className="text-white">2. Ride Details</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateRide} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[#A0A0A0]">Vehicle Type</Label>
                <Select
                  value={rideData.vehicle_type}
                  onValueChange={(value) => setRideData({ ...rideData, vehicle_type: value })}
                >
                  <SelectTrigger className="bg-[#1A1A1E] border-[#2A2A2F] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1E] border-[#2A2A2F]">
                    <SelectItem value="standard" className="text-white">Standard (1x)</SelectItem>
                    <SelectItem value="suv" className="text-white">SUV (1.3x)</SelectItem>
                    <SelectItem value="premium" className="text-white">Premium (1.5x)</SelectItem>
                    <SelectItem value="luxury" className="text-white">Luxury (2x)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#A0A0A0]">Distance (miles)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={rideData.distance_miles}
                    onChange={(e) => setRideData({ ...rideData, distance_miles: e.target.value })}
                    placeholder="0.0"
                    className="bg-[#1A1A1E] border-[#2A2A2F] text-white focus:border-[#D4AF37]"
                    data-testid="ride-distance-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#A0A0A0]">Fare ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={rideData.fare_cents}
                    onChange={(e) => setRideData({ ...rideData, fare_cents: e.target.value })}
                    placeholder="0.00"
                    className="bg-[#1A1A1E] border-[#2A2A2F] text-white focus:border-[#D4AF37]"
                    data-testid="ride-fare-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={creating || !selectedCustomer}
                className="w-full bg-gradient-to-r from-[#D4AF37] to-[#B8942D] text-[#0A0A0B] font-semibold h-12"
                data-testid="create-ride-btn"
              >
                {creating ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating Ride...</>
                ) : (
                  <><Plus className="mr-2" size={18} /> Create Ride & Award Points</>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </StaffLayout>
  );
};

export default StaffRides;
